// @ts-strict-ignore
import type { AllColumnFilters, AllowedFilters } from "@lib/mediaLibrary";
import { allFileTypeExtensions } from "@lib/mediaLibrary";

import type { ColumnFiltersState, SupportedTypes, SupportedExtensions } from "../types";
import { uppercaseFirstLetter } from "./uppercaseFirstLetter";

/**
 * Given an abstract list of allowed filters, generate a DataTable-compatible set of filters
 *
 * This sets up the filter that should be used for "all" of a type within the media library.
 * These filters can then be used as-is in the `initialState` of `useMediaLibraryReducer`
 * @param allowedFilters the output of `extractAllowedFilters`
 * @returns a map of file type to DataTable-compatible filters
 */
export const getDefaultColumnFilters = (allowedFilters: AllowedFilters): AllColumnFilters => {
  let defaultAllIsEmptyString = true;
  const allowedTypes = Object.keys(allowedFilters) as SupportedTypes[];
  const all: AllColumnFilters = { all: [] };

  if (allowedTypes.length > 0 && allowedTypes.length !== Object.keys(allFileTypeExtensions).length) {
    defaultAllIsEmptyString = false;
  }
  const sections = Object.entries(allowedFilters);

  // initial pass to determine if we have selected all possible file types
  sections.forEach(
    ([type, extensions]: [type: SupportedTypes, extensions: { [K in SupportedExtensions]?: true }]) => {
      const fileExtensionList = Object.keys(extensions) as SupportedExtensions[];
      const defaultIsType = fileExtensionList.length === Object.keys(allFileTypeExtensions[type]).length;
      if (!defaultIsType) {
        defaultAllIsEmptyString = false;
      }
    }
  );

  // now create the filters for all of a type
  sections.forEach(
    ([type, extensions]: [type: SupportedTypes, extensions: { [K in SupportedExtensions]?: true }]) => {
      const baseValues = Object.keys(extensions);
      const allExtensions = Object.keys(allFileTypeExtensions[type]);
      const defaultIsType = baseValues.length === allExtensions.length;
      const allFilter: ColumnFiltersState = [{ id: "type", value: uppercaseFirstLetter(type) }];
      // if we only have 1 class of file types, and we are allowing all of them, we can use a generic "all" filter for that type alone
      const useGenericAllFilter =
        (sections.length === 1 && defaultIsType) || (defaultIsType && defaultAllIsEmptyString);
      all[type] = useGenericAllFilter ? allFilter : [{ id: "type", value: baseValues.flatMap((val) => val) }];
    }
  );

  // if we have any file restrictions, this will create a new "all" filter that only includes the restricted types
  all.all = defaultAllIsEmptyString
    ? [{ id: "type", value: "" }]
    : [{ id: "type", value: Object.values(all).flatMap((state) => state.flatMap(({ value }) => value)) }];
  if (sections.length === 0) {
    // add in all the custom filters as well
    all.image = [{ id: "type", value: "Image" }];
    all.video = [{ id: "type", value: "Video" }];
    all.font = [{ id: "type", value: "Font" }];
    all.code = [{ id: "type", value: "Code" }];
    all.document = [{ id: "type", value: "Document" }];
    all.other = [{ id: "type", value: "Other" }];
  }
  return all;
};
